import React, { useState, useEffect } from 'react';
import {
  PlusCircle,
  Edit2,
  Trash2,
  X,
  Image as ImageIcon,
  Calendar,
  User,
  Upload,
  MessageSquare,
  Link as LinkIcon,
  FileText
} from 'lucide-react';
import englishService from '../services/englishService';
import apiService from '../apiService';
import RichTextEditor from '../components/RichTextEditor';

// Alert components remain the same
const Alert = ({ children, variant, className = '' }) => {
  const baseStyles = 'flex w-full rounded-lg border p-4';
  const variantStyles = variant === 'destructive'
    ? 'border-red-200 bg-red-50 text-red-600'
    : 'border-gray-200 bg-gray-50 text-gray-600';

  return (
    <div className={`${baseStyles} ${variantStyles} ${className}`}>
      {children}
    </div>
  );
};

const AlertDescription = ({ children, className = '' }) => (
  <div className={`text-sm ${className}`}>
    {children}
  </div>
);

const SccnMessageManagement = () => {
  // State management remains the same
  const [messages, setMessages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfUploading, setPdfUploading] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    thumbnail: '',
    content: [''],
    AnyLink: '',
    date: new Date().toISOString().split('T')[0],
    publishedBy: '',
    comments: [],
    images: [],
    pdfReport: ''
  });

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await englishService.sccnMessages.getAll();
      
      let messagesData;
      if (Array.isArray(response)) {
        messagesData = response;
      } else if (response.data && Array.isArray(response.data)) {
        messagesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        messagesData = response.data.data;
      } 
    else {
        console.error('Unexpected response structure:', response);
        setError('Invalid data format');
        return;
      }
      
      setMessages(messagesData);
    } catch (error) {
      console.error('Fetch error:', error);
      setError('Failed to fetch messages: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    if (name === 'content') {
      const newContent = [...formData.content];
      newContent[index] = value;
      setFormData(prev => ({ ...prev, content: newContent }));
    } else if (name === 'AnyLink') {
      setFormData(prev => ({ ...prev, AnyLink: value }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${type === 'thumbnail' ? 'Thumbnail' : 'Image'} size should be less than 5MB`);
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === 'thumbnail') {
          setFormData(prev => ({ ...prev, thumbnail: reader.result }));
        } else if (type === 'image') {
          if (formData.images.length >= 5) {
            setError('Maximum 5 images are allowed');
            return;
          }
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            
            let width = img.width;
            let height = img.height;
            
            if (width > height) {
                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }
            } else {
                if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                }
            }
            
            canvas.width = width;
            canvas.height = height;
            
            ctx.drawImage(img, 0, 0, width, height);
            
            const compressedImage = canvas.toDataURL('image/jpeg', 0.7);
            
            setFormData(prev => ({
              ...prev,
              images: [...prev.images, compressedImage]
            }));
          };
          img.src = reader.result;
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    setError('');

    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }

    if (!formData.thumbnail) {
      setError('Thumbnail is required');
      return false;
    }

    if (!formData.content.some(paragraph => paragraph.trim())) {
      setError('At least one content paragraph is required');
      return false;
    }

    if (formData.images && formData.images.length > 5) {
      setError('Maximum 5 images are allowed');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError('');

      const formDataToSend = {
        title: formData.title.trim(),
        thumbnail: formData.thumbnail,
        content: formData.content.filter(paragraph => paragraph.trim() !== ''),
        AnyLink: formData.AnyLink || '',
        date: new Date(formData.date).toISOString(),
        publishedBy: formData.publishedBy?.trim() || '',
        comments: formData.comments || [],
        images: formData.images || [],
        pdfReport: formData.pdfReport || ''
      };

      let response;
      if (selectedMessage) {
        response = await englishService.sccnMessages.update(selectedMessage._id, formDataToSend);
      } else {
        response = await englishService.sccnMessages.create(formDataToSend);
      }

      if (response.success) {
        setSuccess(selectedMessage ? 'Message updated successfully!' : 'Message created successfully!');
        await fetchMessages();
        setShowModal(false);
        resetForm();
      } else {
        throw new Error(response.message || 'Failed to save message');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setError(error.message || 'Failed to save message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (message) => {
    setSelectedMessage(message);
    setFormData({
      title: message.title,
      content: message.content,
      AnyLink: message.AnyLink || '',
      thumbnail: message.thumbnail,
      date: new Date(message.date).toISOString().split('T')[0],
      publishedBy: message.publishedBy || '',
      comments: message.comments || [],
      images: message.images || [],
      pdfReport: message.pdfReport || ''
    });
    setShowModal(true);
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await englishService.sccnMessages.delete(selectedMessage._id);
      setSuccess('Message deleted successfully!');
      fetchMessages();
      setShowDeleteModal(false);
      setSelectedMessage(null);
    } catch (error) {
      setError(error.message || 'Failed to delete message');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      thumbnail: '',
      content: [''],
      AnyLink: '',
      date: new Date().toISOString().split('T')[0],
      publishedBy: '',
      comments: [],
      images: [],
      pdfReport: ''
    });
    setSelectedMessage(null);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('PDF size should be less than 50MB');
      return;
    }

    try {
      setPdfUploading(true);
      setError('');

      const pdfFormData = new FormData();
      pdfFormData.append('pdf', file);

      const response = await apiService.post('/pdf/upload', pdfFormData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        setFormData(prev => ({ ...prev, pdfReport: response.data.filePath }));
        setSuccess('PDF uploaded successfully!');
      } else {
        throw new Error(response.data.message || 'Failed to upload PDF');
      }
    } catch (error) {
      console.error('PDF upload error:', error);
      setError(error.message || 'Failed to upload PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleRemovePdf = async () => {
    if (!formData.pdfReport) return;

    try {
      setLoading(true);
      await apiService.delete('/pdf/delete', { data: { filePath: formData.pdfReport } });
      setFormData(prev => ({ ...prev, pdfReport: '' }));
      setSuccess('PDF removed successfully!');
    } catch (error) {
      console.error('PDF delete error:', error);
      setError('Failed to remove PDF');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">SCCN Messages Management</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
        >
          <PlusCircle className="mr-2" size={20} />
          Add Message
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {success && (
        <Alert className="mb-4 bg-green-50 text-green-700 border-green-200">
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}

      {/* Card Grid Layout */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No messages found. Create one to get started!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {messages.map((message) => (
            <div key={message._id} className="bg-white rounded-lg shadow-lg overflow-hidden border border-gray-200">
              {/* Thumbnail Section */}
              <div className="h-48 overflow-hidden">
                <img
                  src={message.thumbnail || '/api/placeholder/400/200'}
                  alt={message.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content Section */}
              <div className="p-4">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{message.title}</h3>
                
                {/* Message Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-gray-600">
                    <User size={16} className="mr-2" />
                    {message.publishedBy || 'Anonymous'}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar size={16} className="mr-2" />
                    {new Date(message.date).toLocaleDateString()}
                  </div>
                  <div className="flex items-center text-gray-600">
                    <MessageSquare size={16} className="mr-2" />
                    {message.comments?.length || 0} Comments
                  </div>
                </div>

                {/* Content Preview */}
                <div className="mb-4">
                  <p className="text-gray-600 line-clamp-3">
                    {message.content[0]}
                  </p>
                </div>

                {/* Action Links */}
                <div className="flex justify-between items-center">
                  {message.AnyLink ? (
                    <a
                      href={message.AnyLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center text-sky-500 hover:text-sky-600"
                    >
                      <LinkIcon size={16} className="mr-1" />
                      View Link
                    </a>
                  ) : (
                    <div></div>
                  )}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(message)}
                      className="p-2 text-sky-500 hover:bg-sky-50 rounded-full transition-colors"
                    >
                      <Edit2 size={20} />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedMessage(message);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">
                  {selectedMessage ? 'Edit Message' : 'Add Message'}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Published By */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Published By
                  </label>
                  <input
                    type="text"
                    name="publishedBy"
                    value={formData.publishedBy}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Thumbnail */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Thumbnail *
                  </label>
                  <div className="flex items-center space-x-4">
                    {formData.thumbnail && (
                      <img
                        src={formData.thumbnail}
                        alt="Thumbnail preview"
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                    )}
                    <div className="flex-1">
                      <label className="flex flex-col items-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <ImageIcon className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Upload thumbnail</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'thumbnail')}
                          required={!formData.thumbnail}
                        />
                      </label>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Content *
                  </label>
                  <RichTextEditor
                    value={formData.content}
                    onChange={(content) => setFormData(prev => ({ ...prev, content }))}
                    onError={setError}
                  />
                </div>

                {/* Any Link */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Any Link
                  </label>
                  <input
                    type="text"
                    name="AnyLink"
                    value={formData.AnyLink}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                  />
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date *
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    required
                  />
                </div>

                {/* Additional Images */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Images (Max 5)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative">
                        <img
                          src={image}
                          alt={`Additional ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== index)
                            }));
                          }}
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                    {formData.images.length < 5 && (
                      <label className="flex flex-col items-center justify-center p-4 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500">
                        <Upload className="text-gray-400 mb-2" size={24} />
                        <span className="text-sm text-gray-600">Add image</span>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={(e) => handleFileChange(e, 'image')}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {/* Comments Section (Read-only) */}
                {selectedMessage && formData.comments && formData.comments.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Comments
                    </label>
                    <div className="space-y-2">
                      {formData.comments.map((comment, index) => (
                        <div
                          key={index}
                          className="p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                          <div className="flex justify-between items-start">
                            <span className="font-medium text-gray-700">{comment.user}</span>
                            <span className="text-sm text-gray-500">
                              {new Date(comment.date).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="mt-1 text-gray-600">{comment.comment}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* PDF Report Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PDF Report (Optional)
                  </label>
                  {formData.pdfReport ? (
                    <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="text-green-600 mr-3" size={24} />
                        <div>
                          <p className="text-sm font-medium text-green-800">PDF Uploaded</p>
                          <p className="text-xs text-green-600 truncate max-w-xs">{formData.pdfReport}</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemovePdf}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-sky-500 transition-colors">
                      {pdfUploading ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sky-500 mr-2"></div>
                          <span className="text-sm text-gray-600">Uploading PDF...</span>
                        </div>
                      ) : (
                        <>
                          <FileText className="text-gray-400 mb-2" size={32} />
                          <span className="text-sm text-gray-600">Upload PDF Report</span>
                          <span className="text-xs text-gray-400 mt-1">Max 50MB</span>
                          <input
                            type="file"
                            className="hidden"
                            accept="application/pdf"
                            onChange={handlePdfUpload}
                          />
                        </>
                      )}
                    </label>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-6 py-2 bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
                  >
                    {loading ? 'Saving...' : (selectedMessage ? 'Update Message' : 'Create Message')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Delete Message</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{selectedMessage.title}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && !showModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SccnMessageManagement;
